import { Capacitor, registerPlugin } from '@capacitor/core';
import { api } from './api.js';
import { session } from './session.js';
import { storage } from './storage.js';

/**
 * Notificações push (Firebase Cloud Messaging) no app Android e iOS (no iOS o ticket-car-app troca o token
 * da Apple pelo do Firebase, então a API envia igual para os dois).
 * O plugin nativo (@capacitor/push-notifications) vem do ticket-car-app; aqui usamos só a ponte do
 * Capacitor (registerPlugin), então no navegador tudo vira "não suportado" sem erro.
 *
 * Fluxo:
 *   1. Na tela inicial, se a permissão ainda não foi pedida, mostramos um cartão explicando os avisos.
 *      Só depois do "Ativar" o Android mostra o pedido de permissão (Android 13+).
 *   2. Com permissão: register() → evento "registration" com o token → POST /v1/me/devices.
 *      A cada abertura repetimos (o token pode mudar).
 *   3. Toque na notificação → abre a tela de data.route.
 *   4. Sair da conta → DELETE /v1/me/devices (o aparelho para de receber avisos daquela conta).
 */
const Push = registerPlugin('PushNotifications');
const TOKEN_KEY = 'tc.pushToken';
const DISMISS_KEY = 'tc.pushAskDismissed';

const platform = () => (Capacitor.getPlatform() === 'ios' ? 'ios' : 'android');
const supported = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');
let listening = false;
let onOpen = (route) => location.assign(route);
let onForeground = () => {};

async function listen() {
  if (listening) return;
  listening = true;
  await Push.addListener('registration', async ({ value }) => {
    storage.set(TOKEN_KEY, value);
    if (session.isLoggedIn) await api('POST', '/v1/me/devices', { token: value, platform: platform() }).catch(() => {});
  });
  await Push.addListener('registrationError', (e) => console.warn('push: registro falhou', e?.error));
  await Push.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const route = notification?.data?.route;
    if (route && route.startsWith('/')) onOpen(route);
  });
  await Push.addListener('pushNotificationReceived', (n) => onForeground(n));
}

async function registerDevice() {
  await listen();
  await Push.createChannel({ id: 'lembretes', name: 'Lembretes e avisos', description: 'Revisões, documentos e empresas', importance: 4, visibility: 1 }).catch(() => {});
  await Push.register();
}

export const push = {
  get supported() {
    return supported();
  },

  /** Navegação ao tocar numa notificação e aviso com o app aberto (definidos pelo main.js). */
  configure({ open, foreground }) {
    if (open) onOpen = open;
    if (foreground) onForeground = foreground;
  },

  /** 'granted' | 'denied' | 'prompt' | 'unsupported' */
  async permission() {
    if (!supported()) return 'unsupported';
    const { receive } = await Push.checkPermissions();
    return receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'prompt';
  },

  /** A cada abertura (já logado): se tem permissão e o usuário quer receber, registra o aparelho. */
  async sync(user) {
    if (!supported() || !user || user.pushEnabled === false) return;
    if ((await this.permission()) === 'granted') await registerDevice();
  },

  /** Pede a permissão (depois da explicação) e registra. Devolve a permissão final. */
  async enable() {
    if (!supported()) return 'unsupported';
    let perm = await this.permission();
    if (perm === 'prompt') {
      const { receive } = await Push.requestPermissions();
      perm = receive === 'granted' ? 'granted' : 'denied';
    }
    if (perm === 'granted') await registerDevice();
    return perm;
  },

  /** O cartão "Ativar lembretes" some depois de "Agora não". */
  get askDismissed() {
    return Boolean(storage.get(DISMISS_KEY));
  },
  dismissAsk() {
    storage.set(DISMISS_KEY, true);
  },

  /** Antes de sair da conta: desliga este aparelho da conta. */
  async forget() {
    const token = storage.get(TOKEN_KEY);
    if (!token) return;
    storage.remove(TOKEN_KEY);
    await api('DELETE', '/v1/me/devices', { token }).catch(() => {});
    if (supported()) await Push.unregister().catch(() => {});
    listening = false;
    await Push.removeAllListeners?.().catch(() => {});
  },

  /** Notificação de teste (botão no perfil). */
  test: () => api('POST', '/v1/me/devices/test'),
};

session.onBeforeSignOut(() => push.forget());
